import React, { Suspense, lazy, useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Eye, Mail, Play, ChevronLeft, BookOpen, Briefcase, ShoppingBag, ChevronRight, Check } from 'lucide-react';
import AdSlot from '../AdSlot';
import axios from 'axios';

const CryptoWidget = lazy(() => import('../CryptoWidget'));
const HoroscopeWidget = lazy(() => import('../HoroscopeWidget'));
const ZSEWidget = lazy(() => import('../ZSEWidget'));
const FerryWidget = lazy(() => import('../FerryWidget'));
const AirQualityWidget = lazy(() => import('../AirQualityWidget'));
const TrendingCommentsWidget = lazy(() => import('../TrendingCommentsWidget'));
const TuzibabaWidget = lazy(() => import('../TuzibabaWidget'));
const TimesBrandsWidget = lazy(() => import('../TimesBrandsWidget'));
const HomePoll = lazy(() => import('../HomePoll'));

const API = process.env.REACT_APP_BACKEND_URL;

function LazySection({ children, fallback, rootMargin = '200px' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin]);
  return <div ref={ref}>{visible ? children : (fallback || <div className="h-32" />)}</div>;
}

function CatalogueWidget({ catalogues }) {
  const [selected, setSelected] = useState(null);
  if (!catalogues || catalogues.length === 0) return null;
  return (
    <section className="bg-white dark:bg-navy-light rounded-2xl border border-gray-100 dark:border-white/5 p-5" data-testid="catalogue-widget">
      <div className="flex items-center gap-2 mb-4">
        <BookOpen size={16} className="text-sky-accent" />
        <h3 className="font-display text-sm font-bold text-navy dark:text-white tracking-wide uppercase">PDF Katalozi</h3>
      </div>
      {selected ? (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-navy dark:text-white">{selected.title}</h4>
            <button onClick={() => setSelected(null)} className="text-xs text-sky-accent hover:text-sky-accent-dark flex items-center gap-1"><ChevronLeft size={12} /> Natrag</button>
          </div>
          <iframe src={`${API}${selected.url}`} title={selected.title} className="w-full h-[500px] rounded-lg border border-gray-200" data-testid="catalogue-viewer" />
          <div className="flex justify-center gap-2 mt-3">
            <a href={`${API}${selected.url}`} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-sky-accent text-white text-xs font-display font-semibold rounded-lg hover:bg-sky-accent-dark transition-colors">Otvori PDF</a>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {catalogues.map(cat => (
            <button key={cat.id} onClick={() => setSelected(cat)} data-testid={`catalogue-item-${cat.id}`} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-white/5 rounded-xl hover:bg-sky-accent/5 transition-colors text-left group">
              <div className="w-10 h-10 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center justify-center flex-shrink-0"><BookOpen size={18} className="text-red-500" /></div>
              <div className="min-w-0">
                <h4 className="text-sm font-display font-medium text-navy dark:text-white truncate group-hover:text-sky-accent transition-colors">{cat.title}</h4>
                <p className="text-[10px] text-gray-400">{cat.page_count} str.</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function FeaturedJobsWidget({ jobs, t }) {
  if (!jobs || jobs.length === 0) return null;
  return (
    <section className="bg-white dark:bg-navy-light rounded-2xl border border-gray-100 dark:border-white/5 p-5" data-testid="featured-jobs-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Briefcase size={16} className="text-sky-accent" />
          <h3 className="font-display text-sm font-bold text-navy dark:text-white tracking-wide uppercase">{t('featured_jobs')}</h3>
        </div>
        <a href="/vibeposao" className="text-sky-accent text-xs font-display font-semibold hover:text-sky-accent-dark flex items-center gap-1">{t('all_jobs')} <ChevronRight size={12} /></a>
      </div>
      <div className="space-y-2">
        {jobs.slice(0, 4).map(job => (
          <a key={job.id} href={`/vibeposao/jobs/${job.id}`} data-testid={`featured-job-${job.id}`} className="block p-3 bg-gray-50 dark:bg-white/5 rounded-xl hover:bg-sky-accent/5 transition-colors group">
            <h4 className="text-sm font-display font-medium text-navy dark:text-white group-hover:text-sky-accent transition-colors line-clamp-1">{job.title}</h4>
            <div className="flex items-center gap-2 mt-1">
              {job.company && <span className="text-[10px] text-gray-400">{job.company}</span>}
              {job.location && <span className="text-[10px] text-gray-400">· {job.location}</span>}
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function FeaturedClassifiedsWidget({ classifieds, t }) {
  if (!classifieds || classifieds.length === 0) return null;
  return (
    <section className="bg-white dark:bg-navy-light rounded-2xl border border-gray-100 dark:border-white/5 p-5" data-testid="featured-classifieds-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShoppingBag size={16} className="text-sky-accent" />
          <h3 className="font-display text-sm font-bold text-navy dark:text-white tracking-wide uppercase">{t('classifieds')}</h3>
        </div>
        <a href="/classifieds" className="text-sky-accent text-xs font-display font-semibold hover:text-sky-accent-dark flex items-center gap-1">{t('view_all')} <ChevronRight size={12} /></a>
      </div>
      <div className="space-y-2">
        {classifieds.slice(0, 4).map(item => (
          <a key={item.id} href="/classifieds" data-testid={`featured-classified-${item.id}`} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-white/5 rounded-xl hover:bg-sky-accent/5 transition-colors group">
            {item.image && <img src={item.image} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />}
            <div className="min-w-0">
              <h4 className="text-sm font-display font-medium text-navy dark:text-white group-hover:text-sky-accent transition-colors line-clamp-1">{item.title}</h4>
              {item.price && <span className="text-xs font-display font-bold text-sky-accent">{item.price}</span>}
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

export default function SidebarSection({ popularArticles, articles, tags, widgetSettings, catalogues, featuredJobs, featuredClassifieds, t }) {
  const [nlEmail, setNlEmail] = useState('');
  const [nlSubmitted, setNlSubmitted] = useState(false);
  const [nlLoading, setNlLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!nlEmail || !nlEmail.includes('@')) return;
    setNlLoading(true);
    try {
      await axios.post(`${API}/api/newsletter/subscribe`, { email: nlEmail });
      setNlSubmitted(true);
      setNlEmail('');
    } catch {}
    setNlLoading(false);
  };

  return (
    <aside className="lg:col-span-4" data-testid="sidebar">
      <div className="space-y-5">
        <AdSlot width="300" height="250" slot="sidebar-top" />

        {/* Popular Articles */}
        <div className="bg-white dark:bg-navy-light rounded-2xl border border-gray-100 dark:border-white/5 p-5">
          <h3 className="font-display text-sm font-bold text-navy dark:text-white tracking-wide uppercase mb-4 flex items-center gap-2">
            <TrendingUp size={14} className="text-sky-accent" />
            {t('most_read')}
          </h3>
          <div className="space-y-0">
            {(popularArticles.length > 0 ? popularArticles : articles.slice(0, 5)).map((a, i) => (
              <Link key={a.id || a._id || i} to={`/article/${a.slug}`} className="flex items-start gap-3 py-2.5 border-b border-gray-50 dark:border-white/5 last:border-0 group" data-testid={`popular-item-${i}`}>
                <span className="flex-shrink-0 w-6 h-6 bg-navy/5 dark:bg-white/5 text-navy dark:text-white rounded flex items-center justify-center text-[10px] font-display font-bold">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <h4 className="text-[13px] font-display font-medium text-navy dark:text-white leading-snug line-clamp-2 group-hover:text-sky-accent transition-colors">{a.title}</h4>
                  <span className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5"><Eye size={9} /> {a.view_count || 0}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="bg-gradient-to-br from-navy to-navy-light rounded-2xl p-5 text-white" data-testid="newsletter-signup">
          <div className="flex items-center gap-2 mb-3">
            <Mail size={16} className="text-sky-accent" />
            <h3 className="font-display text-sm font-bold tracking-wide uppercase">Newsletter</h3>
          </div>
          {nlSubmitted ? (
            <div className="text-center py-4">
              <Check size={24} className="mx-auto text-green-400 mb-2" />
              <p className="text-sm font-semibold text-white">Hvala vam na pretplati!</p>
              <p className="text-xs text-gray-300 mt-1">Hvala vam što gradite zajednicu s nama.</p>
            </div>
          ) : (
            <>
              <p className="text-gray-300 text-xs mb-3 leading-relaxed">{t('newsletter_desc')}</p>
              <form className="space-y-2" onSubmit={handleNewsletterSubmit}>
                <input type="email" value={nlEmail} onChange={e => setNlEmail(e.target.value)} placeholder={t('email_placeholder')} required data-testid="newsletter-email-input" className="w-full px-3 py-2 rounded-lg text-xs bg-white/10 border border-white/15 text-white placeholder-gray-400 focus:outline-none focus:border-sky-accent focus:ring-1 focus:ring-sky-accent/30 font-display" />
                <button type="submit" disabled={nlLoading} data-testid="newsletter-subscribe-btn" className="w-full px-4 py-2 bg-sky-accent hover:bg-sky-accent-dark text-white rounded-lg text-xs font-display font-semibold transition-colors disabled:opacity-50">{nlLoading ? '...' : t('subscribe')}</button>
              </form>
            </>
          )}
        </div>

        <LazySection><Suspense fallback={<div className="h-40 bg-white dark:bg-navy-light rounded-2xl animate-pulse" />}><CryptoWidget /></Suspense></LazySection>
        <LazySection><Suspense fallback={<div className="h-32 bg-white dark:bg-navy-light rounded-2xl animate-pulse" />}><HoroscopeWidget /></Suspense></LazySection>
        <AdSlot width="300" height="250" slot="sidebar-after-horoscope" />
        <LazySection><Suspense fallback={<div className="h-40 bg-white dark:bg-navy-light rounded-2xl animate-pulse" />}><ZSEWidget /></Suspense></LazySection>
        <AdSlot width="300" height="250" slot="sidebar-after-zse" />
        <LazySection><Suspense fallback={<div className="h-40 bg-white dark:bg-navy-light rounded-2xl animate-pulse" />}><FerryWidget /></Suspense></LazySection>
        <AdSlot width="300" height="250" slot="sidebar-after-ferry" />
        <LazySection><Suspense fallback={<div className="h-32 bg-white dark:bg-navy-light rounded-2xl animate-pulse" />}><AirQualityWidget /></Suspense></LazySection>
        <AdSlot width="300" height="250" slot="sidebar-after-airquality" />
        <LazySection><Suspense fallback={<div className="h-32 bg-white dark:bg-navy-light rounded-2xl animate-pulse" />}><TrendingCommentsWidget /></Suspense></LazySection>
        <AdSlot width="300" height="250" slot="sidebar-after-comments" />
        <LazySection><Suspense fallback={<div className="h-32 bg-white dark:bg-navy-light rounded-2xl animate-pulse" />}><TuzibabaWidget /></Suspense></LazySection>

        {/* Homepage Poll */}
        <LazySection><Suspense fallback={<div className="h-32 bg-white dark:bg-navy-light rounded-2xl animate-pulse" />}><HomePoll /></Suspense></LazySection>

        <LazySection><Suspense fallback={<div className="h-32 bg-white dark:bg-navy-light rounded-2xl animate-pulse" />}><TimesBrandsWidget variant="compact" /></Suspense></LazySection>

        {/* Video/Podcast Teaser */}
        <div className="bg-white dark:bg-navy-light rounded-2xl border border-gray-100 dark:border-white/5 p-5" data-testid="media-teaser">
          <h3 className="font-display text-sm font-bold text-navy dark:text-white tracking-wide uppercase mb-3 flex items-center gap-2">
            <Play size={14} className="text-sky-accent" />
            {t('popular')}
          </h3>
          <div className="space-y-3">
            {[
              { title: t('home_podcast_title'), type: 'Podcast', dur: '24 min' },
              { title: t('home_video_title'), type: 'Video', dur: '12 min' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50 dark:border-white/5 last:border-0">
                <div className="w-10 h-10 rounded-lg bg-sky-accent/10 flex items-center justify-center flex-shrink-0"><Play size={14} className="text-sky-accent" /></div>
                <div className="min-w-0">
                  <h4 className="text-xs font-display font-medium text-navy dark:text-white line-clamp-1">{item.title}</h4>
                  <span className="text-[10px] text-gray-400">{item.type} - {item.dur}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tags Cloud */}
        {tags.length > 0 && (
          <div className="bg-white dark:bg-navy-light rounded-2xl border border-gray-100 dark:border-white/5 p-5" data-testid="tags-cloud">
            <h3 className="font-display text-sm font-bold text-navy dark:text-white tracking-wide uppercase mb-3">{t('tags') || 'Oznake'}</h3>
            <div className="flex flex-wrap gap-1.5">
              {tags.slice(0, 20).map(tag => (
                <Link key={tag.name || tag} to={`/tag/${encodeURIComponent(tag.name || tag)}`} className="px-2.5 py-1 bg-gray-50 dark:bg-white/5 text-navy dark:text-gray-300 text-[10px] font-display font-medium rounded-full hover:bg-sky-accent hover:text-white transition-colors">{tag.name || tag}</Link>
              ))}
            </div>
          </div>
        )}

        <AdSlot width="300" height="250" slot="sidebar-bottom" />
        {widgetSettings.featured_jobs_enabled && <FeaturedJobsWidget jobs={featuredJobs} t={t} />}
        {widgetSettings.featured_classifieds_enabled && <FeaturedClassifiedsWidget classifieds={featuredClassifieds} t={t} />}
        {widgetSettings.pdf_catalogues_enabled && <CatalogueWidget catalogues={catalogues} />}
      </div>
    </aside>
  );
}
